import {
  Configuration,
  DataItem,
  Dimensions,
  Gutter,
  OptionsMaterial,
  Params,
  Parapet,
  PriceCategory,
  Roof,
  Wall,
} from "src/types/configuration";
import ConfiguratorService from "src/services/configuratorService";
import prices from "src/prices";
import { calculateKapschuurRoofBigSideLength } from "src/helpers/configuration";

class CalculationService {
  private static instance: CalculationService;
  private configuratorService?: ConfiguratorService;
  private state?: Configuration;
  private dimensions?: Dimensions;
  private parapet?: Parapet;
  private roof?: Roof;
  private roofOverhang = 0;
  private gutter?: Gutter;
  private walls?: Wall[];
  private storages?: { left: boolean; right: boolean };
  private data: DataItem[] = [];
  private optionsMaterial: OptionsMaterial | undefined;
  private params: Params | undefined;
  private montageRates: Record<PriceCategory, number> = {
    A: 0.33,
    B: 0.27,
    NVT: 0,
  };

  constructor(
    initConfiguration: Configuration,
    configuratorService: ConfiguratorService
  ) {
    if (CalculationService.instance) {
      return CalculationService.instance;
    }

    CalculationService.instance = this;
    this.state = initConfiguration;
    this.configuratorService = configuratorService;
    this.dimensions = initConfiguration.dimensions;
    this.parapet = initConfiguration.parapet;
    this.roof = initConfiguration.roof;
    this.gutter = initConfiguration.gutter;
    this.walls = initConfiguration.walls;
    this.storages = initConfiguration.storages;
    this.optionsMaterial = initConfiguration.optionsMaterial;
    this.params = initConfiguration.params;

    this.data = prices
      .filter((price) => price && price.price > 0)
      .map((p) => {
        return {
          name: p.name,
          label: p.label,
          unit: p.unit,
          price: p.price,
          quantity: 0,
          total: 0,
          category: p.category,
        };
      });

    // this.data.push({
    //   name: "montage",
    //   label: "Montage",
    //   unit: "stuk",
    //   price: 0,
    //   quantity: 0,
    //   total: 0,
    //   category: "NVT",
    // });
  }

  setState(state: Configuration): void {
    this.state = state;
    this.dimensions = state.dimensions;
    this.parapet = state.parapet;
    this.roof = state.roof;
    this.gutter = state.gutter;
    this.walls = state.walls;
    this.storages = state.storages;
    this.optionsMaterial = state.optionsMaterial;
    this.params = state.params;
  }

  getData() {
    return this.data.filter((item) => {
      if (!item) return false;

      // @ts-ignore
      if (this.state.roofType === "platdak") {
        if (item.name === "dakpannen" || item.name === "dakpannen_terracota") {
          return false;
        }
      }

      return true;
    });
  }

  isKapschuur() {
    return this.state?.roofType === "kapschuur";
  }

  getEPDMGlue() {
    const item = this.getPriceItem("epdmGlue");
    return item ? item.price * this.calculatePlatdakRoofSquareInSqMeters() : 0;
  }

  getBlackTrim() {
    const item = this.getPriceItem("daktrim_zwart");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getAluminiumTrim() {
    const item = this.getPriceItem("daktrim_blank");
    return item ? item.price * this.getRoofPerimeterInMeters() : 0;
  }

  getPoer() {
    const item = this.getPriceItem("betonproer");
    if (!item) return 0;
    if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity =
      this.optionsMaterial.poerQuantity > 0 &&
      this.optionsMaterial.poer === "poer"
        ? this.optionsMaterial.poerQuantity
        : 1;
    return item.price * quantity;
  }

  getPoertegel() {
    const item = this.getPriceItem("betontegel");
    if (!item) return 0;
    if (!this.optionsMaterial?.poerQuantity) return item.price;
    const quantity =
      this.optionsMaterial.poerQuantity > 0 &&
      this.optionsMaterial.poer === "poertegel"
        ? this.optionsMaterial.poerQuantity
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsWit() {
    const item = this.getPriceItem("liter_beits_wit");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_wit) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_wit > 0
        ? this.optionsMaterial.liter_beits_wit
        : 1;
    return item ? item.price * quantity : 0;
  }

  getLiterBeitsZwart() {
    const item = this.getPriceItem("liter_beits_zwart");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_zwart) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_zwart > 0
        ? this.optionsMaterial.liter_beits_zwart
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsGray() {
    const item = this.getPriceItem("liter_beits_gray");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_gray) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_gray > 0
        ? this.optionsMaterial.liter_beits_gray
        : 1;
    return item.price * quantity;
  }

  getLiterBeitsDouglas() {
    const item = this.getPriceItem("liter_beits_douglas");
    if (!item) return 0;
    if (!this.optionsMaterial?.liter_beits_douglas) return item.price;
    const quantity =
      this.optionsMaterial.liter_beits_douglas > 0
        ? this.optionsMaterial.liter_beits_douglas
        : 1;
    return item.price * quantity;
  }

  getHemelwaterafvoerPvc() {
    const item = this.getPriceItem("hemelwaterafvoer_pvc");
    if (!item) return 0;
    if (this.isKapschuur() || !this.optionsMaterial?.hemelwaterafvoer_pvc)
      return item.price;
    // 2 stuk if width <= 6m, 4 stuk if width > 6m
    const quantity = (this.dimensions?.width ?? 0) > 6000 ? 4 : 2;
    return item.price * quantity;
  }

  getBetonbandAntraciet() {
    const item = this.getPriceItem("betonband_antraciet");
    if (!item) return 0;
    if (!this.optionsMaterial?.betonband_antraciet) return item.price;
    const quantity =
      this.optionsMaterial.betonband_antraciet > 0
        ? this.optionsMaterial.betonband_antraciet
        : 1;
    return item.price * quantity;
  }

  getMontagePrice() {
    // if (!this.shouldApplyMontage()) {
    //   return 0;
    // }

    return this.roundValue(this.calculateMontageSurchargeFromData());
  }

  getRoofingPrice(name: string) {
    if (!this.data || !this.state) return 0;

    const kapschuurDepthRoofSide =
      this.state.params.kapschuurOverhangLength +
      calculateKapschuurRoofBigSideLength(this.state);
    const width = this.dimensions?.width ?? 0;
    const quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
    let price = 0;

    if (name === "dakpanplaat") {
      price = this.getPriceItem("dakpanprofielen-zwart")?.price ?? 0;
    }

    if (name === "dakpanplaat_rood") {
      price = this.getPriceItem("dakpanprofielen-terracotta")?.price ?? 0;
    }

    if (name === "leiendak") {
      price = this.getPriceItem("shingles-zwart")?.price ?? 0;
    }

    if (name === "leiendak_red") {
      price = this.getPriceItem("shingles-rood")?.price ?? 0;
    }

    if (name === "leiendak_green") {
      price = this.getPriceItem("shingles-groen")?.price ?? 0;
    }

    return price * quantity;
  }

  roundValue(num: number, precision: number = 2) {
    return parseFloat(num.toFixed(precision));
  }

  formatToCurrency(price: number) {
    return price.toLocaleString("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  calculateTotalPrice(): number {
    let total = this.data.reduce((acc, item) => {
      if (!item || item.quantity === 0) return acc;
      return acc + item.total;
    }, 0);

    let montagePrice = 0;
    if (this?.state?.optionsMaterial?.montage)
      montagePrice = this.getMontagePrice();

    const montageAlreadyCounted = this.data.some(
      (row: DataItem) =>
        row && row.name === "montage" && row.quantity && row.quantity > 0
    );

    total = montageAlreadyCounted ? total : total + montagePrice;

    return total;
  }

  getFormatTotalPrice(): string {
    return this.formatToCurrency(this.calculateTotalPrice());
  }

  toMeter = (value: number): number => {
    const meters = value / 1000;

    return parseFloat(meters.toFixed(2));
  };

  toSqMeter(value: number): number {
    const meters = value / 1_000_000;

    return parseFloat(meters.toFixed(2));
  }

  calculateRoofSideLength() {
    const base = this.dimensions?.depth ?? 0;
    const angle = this.dimensions?.angle ?? 40;
    const angleInRadians = (angle * Math.PI) / 180;
    const sideLength =
      (base + this.roofOverhang) / (2 * Math.cos(angleInRadians));

    return parseFloat(sideLength.toFixed(2));
  }

  calculateRoofSquareInSqMeters() {
    const width = this.dimensions?.width ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(2 * sideLength * (width + this.roofOverhang));
  }

  calculatePlatdakRoofSquareInSqMeters() {
    const width = this.getWidthWithOverhang();
    const depth = this.getDepthWithOverhang();
    return this.toSqMeter(width * depth);
  }

  calculateTriangleAreaInSqMeters() {
    const base = this.dimensions?.depth ?? 0;
    const sideLength = this.calculateRoofSideLength();

    return this.toSqMeter(
      (1 / 2) * base * Math.sqrt(4 * sideLength ** 2 - base ** 2)
    );
  }

  getCountVerticalBalks() {
    return this.configuratorService?.getColumnsBalks() ?? 0;
  }

  getWidthWithOverhang() {
    const width = this.dimensions?.width ?? 0;
    const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    const overhangRight = this.dimensions?.overhangRight ?? 0;
    return width + overhangLeft + overhangRight;
  }

  getDepthWithOverhang() {
    const depth = this.dimensions?.depth ?? 0;
    const overhangBack = this.dimensions?.overhangBack ?? 0;
    const overhangFront = this.dimensions?.overhangFront ?? 0;
    return depth + overhangBack + overhangFront;
  }

  getRoofPerimeterInMeters() {
    const width = this.getWidthWithOverhang() * 2;
    const depth = this.getDepthWithOverhang() * 2;
    return this.toMeter(width + depth);
  }

  getPriceItem(key: string) {
    return this.data.find((item) => item && item.name === key);
  }

  calculateConfiguration(): DataItem[] {
    if (!this.data || !this.state) return [];

    const columns = this.configuratorService?.getColumns() ?? 0;
    const width = this.dimensions?.width ?? 0;
    const widthWithOverhang =
      width +
      (this.dimensions?.overhangLeft ?? 0) +
      (this.dimensions?.overhangRight ?? 0);
    const depth = this.dimensions?.depth ?? 0;
    const depthWithOverhang =
      depth +
      (this.dimensions?.overhangBack ?? 0) +
      (this.dimensions?.overhangFront ?? 0);
    const height = this.dimensions?.height ?? 0;
    const overhangLeft = this.dimensions?.overhangLeft ?? 0;
    const overhangRight = this.dimensions?.overhangRight ?? 0;
    const literBeitsWit = this.optionsMaterial?.liter_beits_wit ?? 0;
    const literBeitsZwart = this.optionsMaterial?.liter_beits_zwart ?? 0;
    const literBeitsGray = this.optionsMaterial?.liter_beits_gray ?? 0;
    const literBeitsDouglas = this.optionsMaterial?.liter_beits_douglas ?? 0;
    const daktrim_blank = this.optionsMaterial?.daktrim_blank ?? 0;
    const daktrim_zwart = this.optionsMaterial?.daktrim_zwart ?? 0;
    const hemelwaterafvoerPvc =
      this.optionsMaterial?.hemelwaterafvoer_pvc ?? false;
    const betonbandAntraciet = this.optionsMaterial?.betonband_antraciet ?? 0;
    const kapschuurDepthRoofSide =
      this.state.params.kapschuurOverhangLength +
      calculateKapschuurRoofBigSideLength(this.state);
    const isCommonWall =
      this.storages?.left && this.storages?.right ? columns === 2 : false;

    // @ts-ignore
    this.data = this.data.map((item) => {
      if (!item) return item;

      if (item.name === "betonproer") {
        const quantity =
          this.optionsMaterial?.poer === "poer"
            ? this.optionsMaterial?.poerQuantity
            : 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "betontegel") {
        const quantity =
          this.optionsMaterial?.poer === "poertegel"
            ? this.optionsMaterial?.poerQuantity
            : 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "staanders") {
        const quantity = this.getCountVerticalBalks();

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "ringbalk") {
        let quantity = this.getRoofPerimeterInMeters(); // perimeter

        this.walls?.forEach((wall) => {
          if (wall.inner) {
            quantity += this.toMeter(depth);
          }
        });

        quantity = Math.round(quantity / 4);

        if (this.isKapschuur()) {
          quantity = width * 3; // 3 bulks - 2 front and 1 back
          quantity = this.toMeter(quantity);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "gordingbalken") {
        // step 600 mm
        let quantity = Math.round(widthWithOverhang / 600) * depthWithOverhang;

        if (this.state?.roofType === "kapschuur") {
          quantity = Math.round(widthWithOverhang / 600) * depthWithOverhang;
        }

        // Calculate corner vertical balks at the big vertical balks
        // 80 cm - length 1 corner vertical balks
        // quantity += this.configuratorService ? this.configuratorService?.getEmptyWallsCount() * 2 * 800 : 0;

        // Calculate Overstek Links + Overstek Rechts
        quantity += 4 * (overhangLeft + overhangRight);
        quantity = this.toMeter(quantity);

        if (this.isKapschuur()) {
          quantity =
            Math.round(widthWithOverhang / 500) * kapschuurDepthRoofSide;
          // @ts-ignore
          quantity += this.configuratorService?.getColumns() * 751; // 751 mm - length overhang shoren
          // @ts-ignore
          quantity +=
            (this.configuratorService?.getEmptyWallsCount() || 0) * 2 * 743; // 743 mm - other shoren
          quantity = this.toMeter(quantity);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "boeideel") {
        let quantity = this.getRoofPerimeterInMeters() * 2; // perimeter * 2

        if (this.isKapschuur()) {
          quantity = kapschuurDepthRoofSide * 2 + width; // left and right + back
          quantity = this.toMeter(quantity);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      // carcas wall and gable
      if (item.name === "wood_frame") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;

          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (tempWidth >= 4000) {
            quantity += 8;
          } else if (tempWidth >= 3300) {
            quantity += 7;
          } else if (tempWidth >= 2700) {
            quantity += 6;
          } else {
            quantity += 5;
          }
        });

        quantity = this.toMeter(height * quantity);

        if (this.isKapschuur()) {
          quantity = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let q = 0;

            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (tempWidth >= 4000) {
              q = 8;
            } else if (tempWidth >= 3300) {
              q = 7;
            } else if (tempWidth >= 2700) {
              q = 6;
            } else {
              q = 5;
            }

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              quantity += q * (this.state?.params.kapschuurSmallHeight || 0);
            }

            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              quantity += q * (this.state?.params.kapschuurBigHeight || 0);
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              quantity +=
                (q *
                  ((this.state?.params.kapschuurBigHeight || 0) +
                    (this.state?.params.kapschuurSmallHeight || 0))) /
                2;
            }
          });

          quantity = this.toMeter(quantity);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_potdeksel_black") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;

          let tempHeight = height;

          if (wall.woodWall.outerColor === "wall_potdeksel_black") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }

          if (wall.woodWall.innerColor === "wall_potdeksel_black") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }
        });

        if (this.isKapschuur()) {
          quantity = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let tempHeight = height;

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurSmallHeight;
            }

            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurBigHeight;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempHeight =
                ((this.state?.params.kapschuurBigHeight || 0) +
                  (this.state?.params.kapschuurSmallHeight || 0)) /
                2;
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (wall.woodWall.outerColor === "wall_potdeksel_black") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }

            if (wall.woodWall.innerColor === "wall_potdeksel_black") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }
          });

          quantity = this.toMeter(height * quantity);
        }

        quantity = quantity / 0.19; // 0.19 meters - board height

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_potdeksel") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
          let tempHeight = height;

          if (wall.woodWall.outerColor === "wall_potdeksel") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }

          if (wall.woodWall.innerColor === "wall_potdeksel") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }
        });

        if (this.isKapschuur()) {
          quantity = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let tempHeight = height;

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurSmallHeight;
            }

            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurBigHeight;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempHeight =
                ((this.state?.params.kapschuurBigHeight || 0) +
                  (this.state?.params.kapschuurSmallHeight || 0)) /
                2;
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (wall.woodWall.outerColor === "wall_potdeksel") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }

            if (wall.woodWall.innerColor === "wall_potdeksel") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }
          });

          quantity = this.toMeter(height * quantity);
        }

        quantity = quantity / 0.19; // 0.19 meters - board height

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_blockhut") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
          let tempHeight = height;

          if (wall.woodWall.outerColor === "wall_blockhut") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }

          if (wall.woodWall.innerColor === "wall_blockhut") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }
        });

        if (this.isKapschuur()) {
          quantity = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let tempHeight = height;

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurSmallHeight;
            }

            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurBigHeight;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempHeight =
                ((this.state?.params.kapschuurBigHeight || 0) +
                  (this.state?.params.kapschuurSmallHeight || 0)) /
                2;
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (wall.woodWall.outerColor === "wall_blockhut") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }

            if (wall.woodWall.innerColor === "wall_blockhut") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }
          });

          quantity = this.toMeter(height * quantity);
        }

        quantity = quantity / 0.19; // 0.19 meters - board height

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_blockhut_black") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
          let tempHeight = height;

          if (wall.woodWall.outerColor === "wall_blockhut_black") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }

          if (wall.woodWall.innerColor === "wall_blockhut_black") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }
        });

        if (this.isKapschuur()) {
          quantity = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let tempHeight = height;

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurSmallHeight;
            }

            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurBigHeight;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempHeight =
                ((this.state?.params.kapschuurBigHeight || 0) +
                  (this.state?.params.kapschuurSmallHeight || 0)) /
                2;
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (wall.woodWall.outerColor === "wall_blockhut_black") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }

            if (wall.woodWall.innerColor === "wall_blockhut_black") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }
          });

          quantity = this.toMeter(height * quantity);
        }

        quantity = quantity / 0.19; // 0.19 meters - board height

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "wall_vuren_black") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          const tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
          let tempHeight = height;

          if (wall.woodWall.outerColor === "wall_vuren_black") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }

          if (wall.woodWall.innerColor === "wall_vuren_black") {
            quantity += this.toSqMeter(tempHeight * tempWidth);
          }
        });

        if (this.isKapschuur()) {
          quantity = 0;
          commonWall = false;

          this.walls?.forEach((wall) => {
            if (wall.id === "v-0-1" && commonWall) {
              return;
            }

            if (wall.id === "v-0-1" && isCommonWall) {
              commonWall = true;
            }

            let tempWidth = this.configuratorService?.getWallWidth(wall) ?? 0;
            let tempHeight = height;

            if (wall.id.startsWith("h-1")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurSmallHeight;
            }

            if (wall.id.startsWith("h-0")) {
              // @ts-ignore
              tempHeight = this.state?.params.kapschuurBigHeight;
            }

            if (wall.id.startsWith("v-")) {
              // @ts-ignore
              tempHeight =
                ((this.state?.params.kapschuurBigHeight || 0) +
                  (this.state?.params.kapschuurSmallHeight || 0)) /
                2;
              // @ts-ignore
              tempWidth = depth - 2 * (this.params?.kapschuurColumn || 0);
            }

            if (wall.woodWall.outerColor === "wall_vuren_black") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }

            if (wall.woodWall.innerColor === "wall_vuren_black") {
              quantity += this.toSqMeter(tempHeight * tempWidth);
            }
          });

          quantity = this.toMeter(height * quantity);
        }

        quantity = quantity / 0.19; // 0.19 meters - board height

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_73cm_right") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_73cm" &&
            wall.woodWall.doorSide === "right"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_73cm_left") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_73cm" &&
            wall.woodWall.doorSide === "left"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_100cm_right") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_100cm" &&
            wall.woodWall.doorSide === "right"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dichte_100cm_left") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "dichte_100cm" &&
            wall.woodWall.doorSide === "left"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "stapeldorpeldeur_right") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "stapeldorpeldeur" &&
            wall.woodWall.doorSide === "right"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "stapeldorpeldeur_left") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (
            wall.woodWall.door === "stapeldorpeldeur" &&
            wall.woodWall.doorSide === "left"
          ) {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dubbele_dichte") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.door === "dubbele_dichte") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dubble_stapeldorpeldeur") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.door === "dubble_stapeldorpeldeur") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "raam") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.window === "raam") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "vleugelraam") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.woodWall.window === "vleugelraam") {
            quantity++;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakpanprofielen-zwart") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "dakpanplaat") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakpanprofielen-terracotta") {
        let quantity = 0;

        if (
          this.isKapschuur() &&
          this.state?.roof?.type === "dakpanplaat_rood"
        ) {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "shingles-zwart") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "leiendak") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "shingles-rood") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "leiendak_red") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "shingles-groen") {
        let quantity = 0;

        if (this.isKapschuur() && this.state?.roof?.type === "leiendak_green") {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakbeschot") {
        let quantity = this.calculatePlatdakRoofSquareInSqMeters();

        if (this.isKapschuur()) {
          quantity = this.toSqMeter(width * kapschuurDepthRoofSide);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glaselement") {
        let quantity = 0;
        let commonWall = false;

        this.walls?.forEach((wall) => {
          if (wall.id === "v-0-1" && commonWall) {
            return;
          }

          if (wall.id === "v-0-1" && isCommonWall) {
            commonWall = true;
          }

          if (wall.slidingGlassWall === "glass_door_2m") {
            quantity += 2;
          }

          if (wall.slidingGlassWall === "glass_door_3m") {
            quantity += 3;
          }

          if (wall.slidingGlassWall === "glass_door_4m") {
            quantity += 4;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glasbok_zwart") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (
            wall.slidingGlassWall !== "empty" &&
            wall.slidingGlassWallOptions.glasbok > 0 &&
            wall.slidingGlassWallOptions.glassColor === "zwart"
          ) {
            quantity += wall.slidingGlassWallOptions.glasbok;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glasbok_antraciet") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (
            wall.slidingGlassWall !== "empty" &&
            wall.slidingGlassWallOptions.glasbok > 0 &&
            wall.slidingGlassWallOptions.glassColor === "antraciet"
          ) {
            quantity += wall.slidingGlassWallOptions.glasbok;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "glasbok_wit") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (
            wall.slidingGlassWall !== "empty" &&
            wall.slidingGlassWallOptions.glasbok > 0 &&
            wall.slidingGlassWallOptions.glassColor === "wit"
          ) {
            quantity += wall.slidingGlassWallOptions.glasbok;
          }
        });

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakgoot_zinkkleur") {
        let quantity = 0;

        if (this.isKapschuur() && this.gutter?.enabled) {
          quantity += this.toMeter(width * 2);
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "regenwaterafvoer_zinkkleur") {
        let quantity = 0;

        if (this.isKapschuur() && this.gutter?.enabled) {
          // 2 stuk if width <= 6m, 4 stuk if width > 6m
          quantity = 2;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "dakgoot_zink_zwart") {
        let quantity = 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "regenwaterafvoer_zink_zwart") {
        let quantity = 0;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "epdmGlue") {
        let quantity = 0;

        if (!this.isKapschuur() && this.roof?.epdmGlue === true) {
          quantity = this.calculatePlatdakRoofSquareInSqMeters();
        } else {
          quantity = 0;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "daktrim_blank") {
        let quantity = 0;

        if (!this.isKapschuur() && daktrim_blank === true) {
          quantity = this.getRoofPerimeterInMeters();
        } else {
          quantity = 0;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "daktrim_zwart") {
        let quantity = this.getRoofPerimeterInMeters();

        if (this.isKapschuur() || daktrim_zwart !== true) {
          quantity = 0;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_zwart") {
        let quantity = 0;

        if (literBeitsZwart) {
          quantity = literBeitsZwart;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_wit") {
        let quantity = 0;

        if (literBeitsWit) {
          quantity = literBeitsWit;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_gray") {
        let quantity = 0;

        if (literBeitsGray) {
          quantity = literBeitsGray;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "liter_beits_douglas") {
        let quantity = 0;

        if (literBeitsDouglas) {
          quantity = literBeitsDouglas;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "hemelwaterafvoer_pvc") {
        let quantity = 0;

        if (!this.isKapschuur() && hemelwaterafvoerPvc) {
          // 2 stuk if width <= 6m, 4 stuk if width > 6m
          quantity = (this.dimensions?.width ?? 0) > 6000 ? 2 : 1;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "betonband_antraciet") {
        let quantity = 0;

        if (betonbandAntraciet) {
          quantity = betonbandAntraciet;
        }

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "doorBrushes") {
        let quantity = 0;

        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.doorBrushes) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "stainlessSteelHandle") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.stainlessSteelHandle) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "lockProfile") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.lockProfile) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "schuifrichting") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.schuifrichting) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "contributors") {
        let quantity = 0;
        this.walls?.forEach((wall) => {
          if (wall.slidingGlassWallOptions.contributors) {
            quantity++;
          }
        });
        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }

      if (item.name === "bevestigingsmaterialen") {
        let quantity = 1;

        return {
          ...item,
          quantity: quantity,
          total: quantity * item.price,
        };
      }
    });

    this.applyMontageRow();

    return this.data;
  }

  private shouldApplyMontage() {
    return this.optionsMaterial?.montage === true;
  }

  private getMontageMultiplier(category?: PriceCategory) {
    if (!category) {
      return 0;
    }

    return this.montageRates[category] ?? 0;
  }

  private calculateMontageSurchargeFromData() {
    // if (!this.shouldApplyMontage()) {
    //   return 0;
    // }

    return this.data.reduce((sum, item) => {
      if (!item || item.name === "montage") {
        return sum;
      }

      const multiplier = this.getMontageMultiplier(item.category);
      if (!multiplier || item.total <= 0) {
        return sum;
      }

      return sum + item.total * multiplier;
    }, 0);
  }

  private applyMontageRow() {
    if (!this.shouldApplyMontage()) {
      return;
    }

    const surcharge = this.calculateMontageSurchargeFromData();
    const rounded = this.roundValue(surcharge);

    this.data.push({
      name: "montage",
      label: "Montage",
      unit: "stuk",
      price: rounded,
      quantity: 1,
      total: rounded,
    });
  }
}

export default CalculationService;
